/**
 * ARCHITECTURE: Auth model
 *
 * There is **no JWT and no token in JS-reachable storage**. The browser session is a cookie owned by
 * the site backend: `/login` and `/logout` are *server* routes (not React pages), so signing in and out
 * are full-page navigations, and nothing in this codebase reads or parses the session cookie.
 *
 * **Session probe + CSRF token — `/api/variables`** (`useCSRFHeader`, `services/api/UserData`).
 * Same-origin (`GATSBY_SITE_URL`). It answers with a script body, not JSON, so the values are
 * regex-scraped: `METADEFENDER_TOKEN='…'` is the CSRF token and `SSO='…'` is base64 JSON with the
 * signed-in user (including their account `apikey`). Presence of `SSO` *is* the "am I logged in" signal;
 * its absence sets `isLoggedIn: false`. Fetched with `useSWRImmutable`, so it runs **once per page
 * load** — no interval, no focus/reconnect revalidation. The CSRF token therefore lives as long as the
 * page does; it "refreshes" on reload or an explicit `mutate`, and a token rejected as stale means the
 * app needs a reload, not a retry loop.
 *
 * **Two backends, two credentials — do not mix them:**
 *   - *Site backend* (`GATSBY_SITE_URL`, `/api/*` — blog, incidents, settings, tokens): cookie session
 *     plus the `X-Csrf` header. Cookie-authenticated, therefore CSRF-protected.
 *   - *Platform API* (`GATSBY_MDC_PLATFORM_URL`, `/v4/*` — scans, hashes, sandbox): the `apikey` header,
 *     injected for every SWR request by the `login` middleware in `services/swr` from
 *     `user.portal_api_key ?? user.apikey`. Not cookie-authenticated, so no CSRF token is sent.
 *     `hooks/useApikey` overrides that with an integration's `serviceApikey` when a `serviceId` is in
 *     the URL, so results pages read through the integration's key instead of the user's.
 *
 * **API key vs OAuth client — different things.** The account API key is resolved through
 * `/api/token/apikey` and lands in the user context; for a guest there is no account key, so
 * `generateFingerPrint()` (FingerprintJS `visitorId`, why that dependency is in this file) stands in as
 * a per-browser identifier and is registered with `POST /api/token/{apikey}` (CSRF + captcha) on first
 * 401/404. The OAuth clients in Settings (`api/oauth2/client`) are customer-managed machine credentials
 * for the public API — never used to authenticate this web app's own requests.
 *
 * **Cookies written by the frontend: exactly one.** `__Host-opswat-redirect` holds the URL to come back
 * to across the `/login` round trip (`__Host-` prefix ⇒ secure, host-only, path `/`), and is cleared as
 * soon as `/api/variables` resolves. No API key is ever stored in a cookie by this app.
 *
 * **Browser-extension key flow.** `src/pages/extension/get-apikey/` is a waiting screen only — it holds
 * no auth logic. Its purpose is to park the user on an authenticated same-origin route while the site
 * backend and the extension complete the handoff; the page just tells them not to navigate away.
 *
 * `signOut` clears the SWR cache **before** navigating, so no cached user or key can outlive the
 * session. Keep that order.
 *
 * Changing anything above is a security-boundary change — see AGENTS.md → Security boundaries.
 */
import Cookies from 'js-cookie';
import { clearCache } from 'services/swr';
import { DashboardTabs } from 'services/tabs';

export const signIn = () => {
    if (typeof window !== 'undefined') {
        Cookies.set('__Host-opswat-redirect', `${window.location.origin}/dashboard/${DashboardTabs.Security}`, {
            secure: true,
            path: '/',
        });
        window.location.href = '/login';
    }
};

export const signOut = async () => {
    await clearCache();
    window.location.href = '/logout';
};

export const clearRedirectCookie = () => {
    if (typeof window !== 'undefined') {
        Cookies.set('__Host-opswat-redirect', '', { secure: true, path: '/' });
    }
};

export const generateFingerPrint = async () => {
    const { default: FingerprintJS } = await import('@fingerprintjs/fingerprintjs');
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const apikey = result.visitorId;

    if (!apikey) {
        throw new Error('Failed to generate a fingerprint');
    }
    return apikey;
};

export const b64DecodeUnicode = (input: string) => {
    return decodeURIComponent(
        Array.prototype.map
            .call(atob(input), (c) => {
                return '%' + ('00' + (c.codePointAt(0) ?? 0).toString(16)).slice(-2);
            })
            .join(''),
    );
};
